import React, { useState, useEffect } from "react";
import { X, CircleAlert } from "lucide-react";

interface DeletePageModalProps {
  currentPageIndex: number;
  onConfirmDelete: () => void;
  isDeleting?: boolean;
  isOpen: boolean;
  onClose: () => void;
  isLastPage?: boolean;
}

const DeletePageModal: React.FC<DeletePageModalProps> = ({
  currentPageIndex,
  onConfirmDelete,
  isDeleting = false,
  isOpen,
  onClose,
  isLastPage = false,
}) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setVisible(true);
        });
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!mounted) return null;

  return (
    <div
      id="deletePageModal"
      tabIndex={-1}
      className={`fixed inset-0 z-50 flex justify-center items-start w-full h-screen overflow-y-auto transition-opacity duration-200 ${
        visible ? "bg-black bg-opacity-50 opacity-100" : "opacity-0 pointer-events-none"
      }`}
      aria-hidden={!isOpen}
    >
      <div className="relative p-4 w-full max-w-md max-h-full mt-20">
        <div className={`relative bg-white rounded-lg shadow-sm p-4 md:p-6 transition-all duration-200 ${
          visible ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}>
          <button
            type="button"
            className="absolute top-3 end-2.5 text-body bg-transparent hover:bg-neutral-tertiary hover:text-heading rounded-base text-sm w-9 h-9 inline-flex justify-center items-center"
            onClick={onClose}
          >
            <X />
            <span className="sr-only">Close modal</span>
          </button>

          {/* modal body */}
          <div className="p-4 md:p-5 text-center">
            <CircleAlert className="mx-auto mb-4 text-fg-disabled w-12 h-12" aria-hidden="true" />

            <h3 className="mb-6 text-body">
              {isLastPage ? (
                <>
                  This is the only page in the document. Deleting it will{" "}
                  <strong>delete the entire document</strong>.
                </>
              ) : (
                <>
                  Are you sure you want to delete{" "}
                  <strong>page {currentPageIndex + 1}</strong>?
                </>
              )}
            </h3>

            <div className="flex items-center space-x-4 justify-center">
              <button
                type="button"
                className="text-body bg-neutral-secondary-medium box-border border border-default-medium rounded-xl hover:bg-neutral-tertiary-medium hover:text-heading focus:ring-4 focus:ring-neutral-tertiary shadow-xs font-medium text-sm px-4 py-2.5 focus:outline-none"
                onClick={onClose}
              >
                No, cancel
              </button>

              <button
                type="button"
                className="text-white bg-red-600 hover:bg-red-700 focus:ring-4 focus:ring-red-300 shadow-xs font-medium rounded-xl text-sm px-4 py-2.5 focus:outline-none"
                onClick={onConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, I'm sure"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeletePageModal;
